<template>
  <b-container
    class="commutator-tab"
    fluid
  >
    <div
      v-show="$hasUserPermission('PER013')"
      class="commutator-tab__buttons-wrapper no-internal-print"
    >
      <b-dropdown
        class="commutator-tab__dropdown-button"
        split
        variant="outline-primary"
        :disabled="disableDropdownButton"
        :text="$t('engineering.commutator-maintenance.inform-maintenance')"
        @click="openMaintenanceModal"
      >
        <b-dropdown-item @click="openEditValuesModal">
          <b-btn
            class="my-3 my-sm-0"
            variant="outline-primary"
            block
          >
            {{ $t('engineering.commutator-maintenance.edit-values') }}
          </b-btn>
        </b-dropdown-item>
      </b-dropdown>
    </div>
    <s-loader
      v-show="loading"
      class="text-center mb-2"
      size="lg"
    />
    <accordion-panel
      v-show="!loading"
      class="commutator-tab__section mb-2"
      v-for="(section, sectionIndex) in commutatorInfo.sections"
      :key="sectionIndex"
      :id="section.name"
      :collapsed.sync="collapsedPanels[sectionIndex]"
    >
      <template #title>
        <label class="mb-0">
          {{ section.name }}
        </label>
      </template>
      <div
        v-if="section.fields.length"
        class="commutator-tab__cards"
      >
        <div
          class="commutator-tab__card"
          v-for="(card, cardIndex) in section.fields"
          :key="cardIndex"
          :class="{'commutator-tab__card--alarmed': card.alarmed}"
        >
          <div class="commutator-tab__card-tooltip">
            <info-badge v-if="card.hasMaintenanceSuggestion">
              <span v-html="tooltipText" />
              <span
                class="commutator-tab__card-tooltip-link"
                @click="openPossibleMaintenanceModal"
              >
                {{ $t('general.click-here') }}
              </span>.
            </info-badge>
            <sensor-icon
              v-if="card.sensor"
              class="d-inline-flex"
              :detail="card"
              :unique-id="`${sectionIndex}-${cardIndex}`"
            />
          </div>
          <div
            class="text-center"
            :class="{ 'mt-2': card.sensor }"
          >
            <span
              class="commutator-tab__card-value"
              :class="{'text-red': card.alarmed}"
            >
              {{ formatValue(card.value) || '-' }}
            </span>
            <span
              class="commutator-tab__card-unit"
              :class="{'text-red': card.alarmed}"
            >
              {{ card.unit }}
            </span>
          </div>
          <div
            class="commutator-tab__card-name"
            :class="{'text-red': card.alarmed}"
          >
            {{ card.name }}
          </div>
        </div>
      </div>
      <s-empty-list
        v-else
        class="py-5"
        no-padding
      />
    </accordion-panel>

    <accordion-panel
      v-show="!loading"
      class="commutator-tab__section commutator-tab__section-actions-history"
      id="actions-history-accordion"
      :collapsed.sync="actionsHistoryCollapsed"
    >
      <template #title>
        <label class="mb-0">
          {{ $t('engineering.commutator-maintenance.history-maintenance-actions') }}
        </label>
      </template>
      <actions-history-section
        :loading-table="tableLoading"
        :items="actionsHistorySection.items"
        :total-items="actionsHistorySection.total"
        :asset-module-id="assetModuleId"
        :commutator-id="commutatorId"
        @paging="getMaintenanceHistory($event, true)"
        @refresh="refreshData"
      />
    </accordion-panel>

    <possible-maintenance-modal
      v-model="showPossibleMaintenanceModal"
      :commutator-id="tabInfo.commutatorId"
      @refresh-data="refreshData"
    />
    <maintenance-action-modal
      v-model="showMaintenanceActionModal"
      :maintenance="maintenance"
      :is-loading="actionModalLoading"
      @ok="onCloseMaintenanceActionModal"
    />
    <edit-values-modal
      v-model="showEditValuesModal"
      :commutator-id="tabInfo.commutatorId"
      :total-service-time-value="commutatorInfo.totalServiceTime"
      :performed-maintenance-number-value="commutatorInfo.performedMaintenanceNumber"
      @commutator-values-edited="refreshData(true)"
    />
  </b-container>
</template>

<script>
import globalEventBus from '@/services/globalEventBus';
import events from '@/domain/events/engineering';
import Service
  from '@/domain/services/installation/engineering/commutatorMaintenance/commutatorMaintenance';
import ActionsHistorySection
  from '@/domain/components/installation/engineering/commutatorMaintenance/ActionsHistorySection';
import AccordionPanel from '@/components/AccordionPanel';
import InfoBadge from '@/components/icon/InfoBadge';
import SensorIcon from '@/domain/components/installation/engineering/SensorIcon';
import PossibleMaintenanceModal
  from '@/domain/components/installation/engineering/commutatorMaintenance/modal/PossibleMaintenanceModal';
import MaintenanceActionModal
  from '@/domain/components/installation/engineering/commutatorMaintenance/modal/MaintenanceActionModal';
import EditValuesModal from '@/domain/components/installation/engineering/commutatorMaintenance/modal/EditValuesModal';

export default {
  name: 'CommutatorTab',
  props: {
    tabInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    EditValuesModal,
    MaintenanceActionModal,
    PossibleMaintenanceModal,
    SensorIcon,
    InfoBadge,
    AccordionPanel,
    ActionsHistorySection,
  },
  data: () => ({
    commutatorInfo: {},
    collapsedPanels: [],
    collapsedPanelsMemory: [],
    actionsHistoryCollapsed: true,
    showPossibleMaintenanceModal: false,
    showMaintenanceActionModal: false,
    showEditValuesModal: false,
    maintenance: {},
    loading: false,
    tableLoading: false,
    actionModalLoading: false,
    pageRequest: {
      page: 1,
      pageSize: 25,
    },
    actionsHistorySection: {
      list: [],
      total: 0,
    },
  }),
  computed: {
    tooltipText() {
      const message = this.$t('messages.MI027.message');
      const messageWithoutClickHere = message.split(' ').slice(0, -2).join(' ');
      return `<strong>${this.$t('messages.MI027.title')}:</strong> ${messageWithoutClickHere}`;
    },
    assetModuleId() {
      const { assetModuleId } = this.$route.params;
      return assetModuleId;
    },
    commutatorId() {
      const { commutatorId } = this.tabInfo;
      return commutatorId;
    },
    disableDropdownButton() {
      return this.loading || this.actionModalLoading;
    },
  },
  created() {
    this.initListeners();
    this.handlePrintEvents();
    this.refreshData(true);
  },
  beforeDestroy() {
    this.removeListeners();
  },
  methods: {
    handlePrintEvents() {
      window.onbeforeprint = () => {
        this.collapsedPanels = this.collapsedPanels.map(() => false);
        this.actionsHistoryCollapsed = false;
      };
      window.onafterprint = () => {
        this.collapsedPanels = this.collapsedPanelsMemory;
        this.actionsHistoryCollapsed = this.actionsHistoryCollapsedMemory;
      };
    },
    initListeners() {
      globalEventBus.on(events.refreshDataTab, this.refreshData);
      globalEventBus.on(events.print, this.openAllAccordions, this);
    },
    removeListeners() {
      globalEventBus.off(events.refreshDataTab, this.refreshData);
      globalEventBus.off(events.print, this.openAllAccordions, this);
    },
    saveAccordionStatus() {
      this.collapsedPanelsMemory = [];
      this.actionsHistoryCollapsedMemory = [];
      this.collapsedPanelsMemory = this.collapsedPanels;
      this.actionsHistoryCollapsedMemory = this.actionsHistoryCollapsed;
    },
    openAllAccordions() {
      this.saveAccordionStatus();
      this.collapsedPanels = this.collapsedPanels.map(() => false);
      this.actionsHistoryCollapsed = false;
    },
    refreshData(showLoading = false) {
      this.getCards(showLoading);
      this.getMaintenanceAction(showLoading);
      this.getMaintenanceHistory(null, showLoading);
    },
    getCards(showLoading = false) {
      this.loading = showLoading;
      const { assetModuleId } = this.$route.params;
      Service.getCommutatorById(assetModuleId, this.tabInfo.commutatorId)
        .then((response) => {
          this.commutatorInfo = response;
          this.setCollapsedPanels();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setCollapsedPanels() {
      if (this.collapsedPanels.length) return;
      const sections = this.commutatorInfo.sections || [];
      this.collapsedPanels = sections.map(() => true);
    },
    getMaintenanceHistory(pageRequest, showLoading) {
      const payload = pageRequest || this.pageRequest;
      this.tableLoading = showLoading;
      Service.getCommutatorActionsList(this.assetModuleId, this.commutatorId, payload)
        .then(({ data, total }) => {
          this.actionsHistorySection.items = data;
          this.actionsHistorySection.total = total;
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },
    openMaintenanceModal() {
      this.showMaintenanceActionModal = true;
    },
    openEditValuesModal() {
      this.showEditValuesModal = true;
    },
    openPossibleMaintenanceModal() {
      this.showPossibleMaintenanceModal = true;
    },
    onCloseMaintenanceActionModal() {
      this.showPossibleMaintenanceModal = false;
      this.refreshData();
    },
    getMaintenanceAction() {
      const { assetModuleId } = this.$route.params;
      this.actionModalLoading = true;
      Service.getCommutatorMaintenanceAction(assetModuleId, this.tabInfo.commutatorId)
        .then((response) => {
          this.maintenance = response;
        })
        .finally(() => {
          this.actionModalLoading = false;
        });
    },
    formatValue(value) {
      if (!value) return null;
      return this.$n(value, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"
@import "src/styles/defaults/variables"

.commutator-tab
  &__buttons-wrapper
    display: flex
    justify-content: flex-end
    margin-bottom: 15px
  &__section
    box-shadow: none !important
    margin-top: 5px
    &.commutator-tab__section-actions-history
      z-index: 10
  &__cards
    display: grid
    gap: 18px
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr))
    +media-min-sm
      margin: 0 10px
  &__card
    position: relative
    padding: 10px
    border: 1px solid var(--color-border-dark)
    border-radius: 5px
    min-height: 160px
    display: flex
    flex-direction: column
    justify-content: center
    align-items: center
    &--alarmed
      border: 1px solid var(--color-danger) !important
  &__card-tooltip-link
    color: $color-link
    text-decoration: underline
    cursor: pointer
    display: inline-block
  &__card-tooltip
    position: absolute
    top: 5px
    right: 5px
    display: flex
    align-items: flex-start
  &__card-value
    font-size: 2.5em
  &__card-unit
    font-size: 1.8em
  &__card-name
    text-align: center
    margin-top: 10px
    font-size: .9em
    font-weight: bold
  ::v-deep
    .accordion-panel__header
      padding: 0 10px !important
      +media-min-md
        height: 30px
    .commutator-tab__buttons-wrapper
      .dropdown-menu.show
        padding: 0
        width: 100%
      .dropdown-item
        padding: 0
    .commutator-tab__section-actions-history
      .accordion-panel
        &__z-index
          z-index: 10
  @media print
    .commutator-tab
      page-break-after: always
      &__section
        display: block
        page-break-inside: avoid
.manage-fonts_18px
  .commutator-tab__cards
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr))
</style>
